import AppSection from '@/components/app/AppSection'
import { useState, useEffect } from 'react'
import useRequest from '@/utils/useRequest'
import { useRecoilState } from 'recoil'
import { videosState, modalState, createItem, playlistsState } from '@/store'
import {
  Clock,
  Inbox,
  FolderPlus,
  Folder,
  ArrowLeft,
  Grid,
  List,
} from 'react-feather'
import Link from 'next/link'
import { getTimeFromSeconds } from '@/utils/index'
import api from '@/utils/api'
import analytics from '@/utils/analytics'
import Input from '@/components/Input'
import Button from '@/components/Button'
import Tag from '@/components/Tag'
import toast from 'react-hot-toast'
import VideoOptions from '@/pages/videos/VideoOptions'
import VideosOptions from '@/pages/videos/VideosOptions'
import PlaylistOptions from '@/pages/videos/PlaylistOptions'
import useStateWithLocalStorage from '@/utils/useStateWithLocalStorage'
import VideosTagsFilter from '@/pages/videos/VideosTagsFilter'

/* eslint-disable @next/next/no-img-element */

export default function Videos() {
  const [filter, setFilter] = useStateWithLocalStorage({
    key: 'videos-filter',
    initialValue: 'all',
  })
  const [selectedTags, setSelectedTags] = useStateWithLocalStorage({
    key: 'videos-filter-tags',
    initialValue: [],
  })
  const [query, setQuery] = useState('')
  const [selectedPlaylist, setSelectedPlaylist] = useState(null)
  const [viewMode, setViewMode] = useStateWithLocalStorage({
    key: 'videos-view-mode',
    initialValue: 'grid',
  })
  const [videos, setVideos, mutateVideos] = useVideos()
  const [playlists, setPlaylists, mutatePlaylists] = usePlaylists()

  const isLoading = !videos || !playlists

  function getMatchesTags({ videoTags }) {
    return selectedTags.every((selectedTag) =>
      videoTags.some((videoTag) => videoTag.value === selectedTag)
    )
  }

  function filterVideo({ title, archived, finished, tags }) {
    const matchesTitle = title.toLowerCase().includes(query.toLowerCase())
    const matchesArchived = filter === 'archived' ? archived : true
    const matchesUnarchived = filter === 'unarchived' ? !archived : true
    const matchesFinished = filter === 'finished' ? finished : true
    const matchesUnfinished = filter === 'unfinished' ? !finished : true
    const matchesTags = selectedTags.length
      ? getMatchesTags({ videoTags: tags })
      : true

    return (
      matchesTitle &&
      matchesArchived &&
      matchesUnarchived &&
      matchesFinished &&
      matchesUnfinished &&
      matchesTags
    )
  }

  const filteredPlaylists = playlists
    ?.filter(({ title, videos }) => {
      const matchesPlaylist = title.toLowerCase().includes(query.toLowerCase())
      const matchesVideoTitle = videos.find((video) => {
        return video.title.toLowerCase().includes(query.toLowerCase())
      })

      return matchesPlaylist || matchesVideoTitle
    })
    .map((playlist) => {
      return {
        ...playlist,
        videos: playlist.videos?.filter(filterVideo) ?? [],
      }
    })

  function getVideosList() {
    if (selectedPlaylist) {
      const playlist = filteredPlaylists.find(
        ({ _id }) => _id === selectedPlaylist._id
      )
      return playlist?.videos ?? []
    }

    if (!videos) {
      return []
    }

    return videos.filter(filterVideo)
  }

  const title = selectedPlaylist?.title ?? 'Videos'

  return (
    <AppSection
      title={title}
      canonical="/videos"
      isLoading={isLoading}
      headerLeft={
        !isLoading && (
          <div className="flex w-full z-30 relative">
            <VideosOptions
              {...{
                filter,
                setFilter,
                className: 'ml-3 min-w-max',
              }}
            />
            <VideosTagsFilter
              {...{
                className: 'ml-3 min-w-max',
                selectedTags,
                setSelectedTags,
              }}
            />
          </div>
        )
      }
      headerRight={
        !isLoading && (
          <>
            <NewPlaylistButton {...{ setPlaylists }} />
            <div className="flex items-center mr-3">
              <button
                onClick={() => setViewMode('grid')}
                title="Grid view"
                className={`p-1.5 rounded transition ${
                  viewMode === 'grid'
                    ? 'text-primary bg-lighterblue'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Grid style={{ width: '18px', height: '18px' }} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                title="List view"
                className={`p-1.5 rounded transition ${
                  viewMode === 'list'
                    ? 'text-primary bg-lighterblue'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <List style={{ width: '18px', height: '18px' }} />
              </button>
            </div>
            <Input
              type="search"
              name="query"
              id="query"
              placeholder="Search"
              onChange={({ target }) => setQuery(target.value)}
            />
          </>
        )
      }
    >
      {!selectedPlaylist && (
        <PlaylistsList
          {...{
            playlists: filteredPlaylists,
            setSelectedPlaylist,
            mutatePlaylists,
            setPlaylists,
          }}
        />
      )}

      {selectedPlaylist && (
        <div
          className="mb-5 cursor-pointer inline-flex clickable-opacity"
          onClick={() => setSelectedPlaylist(null)}
        >
          <ArrowLeft className="mr-2" />
          Go back
        </div>
      )}

      <VideosList
        {...{
          videos: getVideosList(),
          playlists: filteredPlaylists,
          selectedPlaylist,
          setVideos,
          mutateVideos,
          setPlaylists,
          viewMode,
        }}
      />
    </AppSection>
  )
}

function NewPlaylistButton({ setPlaylists }) {
  const [, setModal] = useRecoilState(modalState)

  function onPlaylistCreated(response) {
    const newPlaylist = response.data.data
    setPlaylists((prev) =>
      createItem({
        items: prev,
        item: {
          ...newPlaylist,
          videos: newPlaylist.videos || [],
        },
      })
    )
  }

  function createPlaylist(title) {
    toast.promise(api.post('/playlists', { title }).then(onPlaylistCreated), {
      loading: 'Creating...',
      success: 'Playlist created',
      error: 'Could not save',
    })

    setModal(null)
  }

  function showTitleModal() {
    setModal({
      isEdit: true,
      title: 'Playlist Title',
      value: '',
      onClick: (e, newTitle) => createPlaylist(newTitle),
    })
  }

  return (
    <Button className="md:ml-5 mr-3 whitespace-nowrap" onClick={showTitleModal}>
      <FolderPlus className="mr-2 h-5 w-5" />
      New Playlist
    </Button>
  )
}

function PlaylistsList({
  playlists,
  setSelectedPlaylist,
  mutatePlaylists,
  setPlaylists,
}) {
  if (!playlists?.length) {
    return null
  }

  return (
    <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {playlists.map((playlist) => (
        <PlaylistCard
          key={playlist._id}
          {...{
            playlist,
            setSelectedPlaylist,
            mutatePlaylists,
            setPlaylists,
          }}
        />
      ))}
    </div>
  )
}

function VideosList({
  videos,
  playlists,
  setVideos,
  mutateVideos,
  setPlaylists,
  selectedPlaylist,
  viewMode,
}) {
  const noVideosInPlaylist = selectedPlaylist && !videos.length

  if (!videos) {
    return (
      <EmptyBox>
        No videos. Please,{' '}
        <Link href="/new">
          <a className="text-primary">create one</a>
        </Link>
        .
      </EmptyBox>
    )
  }

  if (videos.length) {
    if (viewMode === 'list') {
      return (
        <div className="flex flex-col gap-2">
          {videos?.map((video) => (
            <VideoCardList
              key={video._id}
              {...{
                video,
                setVideos,
                mutateVideos,
                setPlaylists,
              }}
            />
          ))}
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {videos?.map((video) => (
          <VideoCard
            key={video._id}
            {...{
              video,
              setVideos,
              mutateVideos,
              setPlaylists,
            }}
          />
        ))}
      </div>
    )
  } else if (noVideosInPlaylist || !playlists.length) {
    return <EmptyBox>No Videos</EmptyBox>
  } else {
    return null
  }
}

function EmptyBox({ children }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-10 flex flex-col center h-full w-full pt-14">
      <Inbox
        className="mb-5 text-gray-600 stroke-1"
        style={{ width: '50px', height: '50px' }}
      />
      <span className="text-xl text-gray-600">{children}</span>
    </div>
  )
}

function PlaylistCard({
  playlist,
  setSelectedPlaylist,
  setPlaylists,
  mutatePlaylists,
}) {
  return (
    <a
      onClick={() => {
        analytics.track('Playlist card clicked')
        setSelectedPlaylist(playlist)
      }}
    >
      <div className="shadow-md flex flex-col p-5 rounded-lg cursor-pointer bg-white w-30 border-2 border-transparent transition hover:border-primary">
        <div className="flex items-center font-medium text-black">
          <div className="mr-2 rounded-full center bg-gray-300 box-content text-black w-auto p-2 h-2 text-sm">
            {playlist.videos.length}
          </div>
          <Folder className="h-5 w-5 mr-1" style={{ minWidth: '20px' }} />
          <h3 className="truncate mr-1" title={playlist.title}>
            {playlist.title}
          </h3>
          <PlaylistOptions
            {...{
              playlist,
              setPlaylists,
              mutatePlaylists,
              className: 'ml-auto',
            }}
          />
        </div>

        <div className="mt-5 center w-full">
          <PlaylistProgress {...{ playlist }} />
        </div>
      </div>
    </a>
  )
}

function VideoCard({ video, setVideos, mutateVideos, setPlaylists }) {
  const thumbnailURL = video.thumbnails.medium
    ? video.thumbnails.medium.url
    : video.thumbnails.default.url

  return (
    <Link href={`/videos/${video._id}`}>
      <a onClick={() => analytics.track('Video card clicked')}>
        <div className="shadow-md flex flex-col px-5 py-3 rounded-lg bg-white w-30 border-2 border-transparent transition hover:border-primary">
          <div className="flex font-medium text-black">
            <h3 className="truncate mr-2" title={video.title}>
              {video.title}
            </h3>
            <VideoOptions
              {...{
                video,
                setVideos,
                mutateVideos,
                setPlaylists,
                className: 'ml-auto',
              }}
            />
          </div>
          <div className="mt-3 center w-full">
            <div className="flex flex-col">
              <img
                className="rounded-lg"
                width={320}
                height={180}
                src={thumbnailURL}
                alt="thumbnail"
              />
              <VideoProgress video={video} />
            </div>
          </div>
          <Tags tags={video.tags} />
          <div className="flex">
            <div className="truncate text-sm">{video.channelTitle}</div>
            <div className="ml-auto flex text-sm center">
              <Clock
                className="mr-1"
                style={{ width: '15px', minWidth: '15px' }}
              />
              {getTimeFromSeconds(video.duration)}
            </div>
          </div>
        </div>
      </a>
    </Link>
  )
}

function VideoCardList({ video, setVideos, mutateVideos, setPlaylists }) {
  const thumbnailURL = video.thumbnails.medium
    ? video.thumbnails.medium.url
    : video.thumbnails.default.url

  return (
    <Link href={`/videos/${video._id}`}>
      <a onClick={() => analytics.track('Video card clicked')}>
        <div className="shadow-sm flex items-center px-4 py-3 rounded-lg bg-white border-2 border-transparent transition hover:border-primary gap-4">
          <div className="flex-shrink-0 relative">
            <img
              className="rounded-md"
              width={120}
              height={68}
              src={thumbnailURL}
              alt="thumbnail"
            />
            <VideoProgress video={video} />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex items-start">
              <h3
                className="font-medium text-black truncate mr-2 flex-1"
                title={video.title}
              >
                {video.title}
              </h3>
              <VideoOptions
                {...{
                  video,
                  setVideos,
                  mutateVideos,
                  setPlaylists,
                  className: 'ml-auto flex-shrink-0',
                }}
              />
            </div>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-gray-500 truncate">
                {video.channelTitle}
              </span>
              <span className="flex items-center text-sm text-gray-500 flex-shrink-0">
                <Clock
                  className="mr-1"
                  style={{ width: '13px', minWidth: '13px' }}
                />
                {getTimeFromSeconds(video.duration)}
              </span>
            </div>
            <Tags tags={video.tags} />
          </div>
        </div>
      </a>
    </Link>
  )
}

function Tags({ tags }) {
  return (
    <div className="tags my-3 truncate">
      {!tags.length && <div className="h-6" />}
      {tags.map(({ value, label }, index) => {
        const className = index > 0 ? 'ml-1' : ''

        return (
          <Tag key={value} className={className}>
            {label}
          </Tag>
        )
      })}
    </div>
  )
}

function getVideoProgressPercentage(video) {
  const { lastProgress } = video

  if (!lastProgress) {
    return 0
  }

  const { duration } = video
  return (lastProgress * 100) / duration
}

function PlaylistProgress({ playlist }) {
  const { videos } = playlist

  const allPercentages = videos.map(getVideoProgressPercentage)

  const percentageSum = allPercentages.reduce(
    (acc, videoPercentage) => acc + videoPercentage,
    0
  )

  const percentage = percentageSum / videos.length || 0

  return (
    <div className="h-1 w-full relative mt-2">
      <div className="absolute bg-gray-300 inset-0 w-full rounded-lg" />
      <div
        className="absolute bg-primary inset-0 rounded-lg max-w-full"
        style={{ width: `${percentage}%` }}
      />
    </div>
  )
}

function VideoProgress({ video }) {
  const { lastProgress } = video
  const { duration } = video
  const percentage = (lastProgress * 100) / duration

  // To keep same size between video cards
  if (!lastProgress) {
    return <div className="mt-2 h-1" />
  }

  return (
    <div className="h-1 w-full relative mt-2">
      <div className="absolute bg-gray-300 inset-0 w-full rounded-lg" />
      <div
        className="absolute bg-primary inset-0 rounded-lg max-w-full"
        style={{ width: `${percentage}%` }}
      />
    </div>
  )
}

function useVideos() {
  const { data: serverVideos, mutate } = useRequest('/api/videos')
  const [videos, setVideos] = useRecoilState(videosState)

  useEffect(() => {
    if (serverVideos) {
      setVideos(serverVideos)
    }
  }, [serverVideos])

  return [videos, setVideos, mutate]
}

function usePlaylists() {
  const { data: serverPlaylists, mutate } = useRequest('/api/playlists')
  const [playlists, setPlaylists] = useRecoilState(playlistsState)

  useEffect(() => {
    if (serverPlaylists) {
      setPlaylists(serverPlaylists)
    }
  }, [serverPlaylists])

  return [playlists, setPlaylists, mutate]
}
